import { AUDIT_ISSUE_DEFINITIONS } from './audit-issue-definitions';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { AuditIssue, AuditPage, AuditPageResult, AuditScanContext, PageLinksRequest, PageRequest, PagesByResourceRequest, ResourceRequest } from '../models/on-page';
import { SeoApiService } from '../seo-api.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OnPageService {

  private _auditDetails: any;
  private _auditPages: any;
  private _pageSummaryInfo: any[];
  private _selectedPage: any;
  private _scanedPages: any[];

  public constructor(private seoApiService: SeoApiService, private utils: UtilsService, private translate: TranslateService,
    private httpClient: HttpClient, private cache: InMemoryCacheService) { }

  public async getSummary(payload: any,):Promise<any> {
    this.cache.loading = true;
    const {data, error} = await this.seoApiService.getPageSummary(payload);
    this.cache.loading = false;
    this._auditDetails = data;
    if (error) this.utils.handleApiError(error, 'Unable to retrieve the audit summary.');
    return data;
  }

  public async getPages(payload: PageRequest, scanId: number):Promise<any> {
    this.cache.loading = true;
    const {data, error} = await this.seoApiService.getPages(payload, scanId);
    this.cache.loading = false;
    if (error) {
      this.utils.errorMessage(MessageType.TOAST, `There was an issue getting the requested pages.`);
      this.utils.logger.error(`There was an issue getting the requested pages.`, error);
    } else {
      this._auditPages = data;
      this.getPageSummaryInfo();
    }

    return {data, error};
  }

  public async getAllPages(payload: PageRequest, scanId: number): Promise<{ data: AuditPageResult | null; error: unknown }> {
    this._pageSummaryInfo = [];
    this._auditPages = null;
    const items: AuditPage[] = [];
    let total = Infinity;
    try {
      while (items.length < total) {
        const response = await this.seoApiService.getPages({ ...payload, limit: 1000, offset: items.length }, scanId);
        if (response.error) return { data: null, error: response.error };
        const result = response.data as AuditPageResult | null;
        if (!result || !Array.isArray(result.items)) throw new Error('Page results are unavailable.');
        if (!Number.isInteger(result.totalItemsCount) || result.totalItemsCount < 0) throw new Error('Page result totals are unavailable.');
        total = result.totalItemsCount;
        if (!result.items.length && items.length < total) throw new Error('Page results are incomplete.');
        items.push(...result.items);
        if (items.length >= total) {
          this._auditPages = { ...result, items, itemsCount: items.length };
          this.getPageSummaryInfo();
          return { data: this._auditPages, error: null };
        }
      }
    } catch (error) { return { data: null, error }; }
    return { data: null, error: new Error('Page results are unavailable.') };
  }

  public async getEvidence(scanId: number, request: import('../models/on-page').AuditEvidenceRequest): Promise<{ data: import('../models/on-page').AuditEvidenceResult | null; error: unknown }> {
    return this.seoApiService.getAuditEvidence(scanId, request);
  }

  public async getScan(scanId: number): Promise<{ data: AuditScanContext | null; error: unknown }> {
    return this.seoApiService.getAuditScan(scanId);
  }

  public async getPageLinks(payload: PageLinksRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPageLinks(payload, scanId);;
  }

  public async getPagesByResource(payload: PagesByResourceRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPagesByResource(payload, scanId);;
  }

  public async getResources(payload: ResourceRequest, scanId: number):Promise<any> {
    return await this.seoApiService.getPageResources(payload, scanId);;
  }

  public async getLatestScan(payload: string):Promise<any> {
    const scanRecord = await this.seoApiService.getLatestScan(payload);
    return scanRecord;
  }

  public async getPageSpeedInsights(url: string, strategy: string, refresh: boolean = false):Promise<any> {
    const pageSpeedInsights = await this.seoApiService.getPageSpeedInsights(url, strategy, false, refresh);
    return pageSpeedInsights;
  }

  public getHowToFix(page: string): Observable<any> {
    return this.httpClient.get(`assets/site-audit-fixes/${page}.html`, {responseType: 'text'});
  }

  public getPageFindings(page: AuditPage): AuditIssue[] {
    return AUDIT_ISSUE_DEFINITIONS.filter(definition => definition.field !== 'seoFriendlyUrl')
      .filter(definition => (definition.checks ? page.checks?.[definition.field] : page[definition.field as keyof AuditPage]) === true)
      .map(definition => ({ name: this.translate.instant(definition.label), title: this.translate.instant(definition.label),
        indicator: definition.severity, description: 1, filter: '', howToFixPath: definition.help }));
  }

  private getPageSummaryInfo() {
    this._pageSummaryInfo = [];
    this.auditPages?.items?.forEach(page => {
      const issueCount = [
        ...Object.values(page?.checks ?? {}),
        page?.brokenLinks,
        page?.brokenResources,
        page?.duplicateTitle,
        page?.duplicateDescription,
        page?.duplicateContent
      ].filter(Boolean).length;

      const infoItem = {
        url: page?.url,
        title: page?.meta?.title,
        score: page?.onPageScore,
        description: page?.meta?.description,
        titleLength: page?.meta?.titleLength,
        checks: page?.checks,
        statusCode: page?.statusCode,
        issueCount,
        internalLinks: page?.meta?.internalLinksCount,
        externalLinks: page?.meta?.externalLinksCount
      }

      this._pageSummaryInfo.push(infoItem);
    });
    return this._pageSummaryInfo;
  }

  public set selectedPage(value: any) {
    this._selectedPage = value;
  }

  public get selectedPage(): any {
    return this._selectedPage;
  }

  public get auditDetails() {
    return this._auditDetails;
  }

  public get auditPages() {
    return this._auditPages;
  }

  public get summaryInfo() {
    if (!this.auditDetails) return null;
    return {
      pagesCrawled: this.auditDetails?.crawlStatus?.pagesCrawled,
      pagesInQueue: this.auditDetails?.crawlStatus?.pagesInQueue,
      maxCrawlPages: this.auditDetails?.crawlStatus?.maxCrawlPages,
      crawlProgress: this.auditDetails?.crawlProgress,
      lastCrawlTime: this.auditDetails?.domainInfo?.crawlEnd ?? this.auditDetails?.domainInfo?.crawlStart,
      domainCrawled: this.auditDetails?.domainInfo?.mainDomain,
      mode: this.auditDetails?.crawlOptions == null ? 'Crawl mode not recorded' : this.auditDetails.crawlOptions.enableBrowserRendering ? 'Browser rendering' : this.auditDetails.crawlOptions.enableJavascript ? 'JavaScript enabled' : 'HTML-only; JavaScript not executed',
      score: this.auditDetails?.pageMetrics?.onPageScore
    }
  }

  public get pageSummaryInfo() {
    return this._pageSummaryInfo;
  }

  public get siteLevelIssues() {
    return [{
          "name": this.translate.instant('onpage.summaryAudit.sitemap'),
          "indicator": this.auditDetails?.domainInfo?.checks?.sitemap == null ? 'NOT_CHECKED' : this.auditDetails?.domainInfo?.checks?.sitemap ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.sitemapDetails'),
          "description": "",
          "howToFixPath":"sitemap-notfound"
      },
      {
          "name": this.translate.instant('onpage.summaryAudit.robots'),
          "indicator": this.auditDetails?.domainInfo?.checks?.robotsTxt == null ? 'NOT_CHECKED' : this.auditDetails?.domainInfo?.checks?.robotsTxt ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.robotsDetails'),
          "description": "",
          "howToFixPath":"invalid-robots-txt-file"
      },{
          "name": this.translate.instant('onpage.summaryAudit.sslCertificate'),
          "indicator": this.auditDetails?.domainInfo?.checks?.ssl == null ? 'NOT_CHECKED' : this.auditDetails?.domainInfo?.checks?.ssl ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.sslCertificateDetails'),
          "description": "",
          "howToFixPath":"invalid-ssl-certificate"
      },{
          "name": this.translate.instant('onpage.summaryAudit.favicon'),
          "indicator": this.auditDetails?.pageMetrics?.checks?.noFavicon == null ? 'NOT_CHECKED' : this.auditDetails.pageMetrics.checks.noFavicon === 0 ? 'FOUND' : 'NOT_FOUND',
          "details": this.translate.instant('onpage.summaryAudit.faviconDetails'),
          "description": "",
          "howToFixPath":"no-favicon"
    }]
  }

  public get pageLevelIssues(): AuditIssue[] {
    return AUDIT_ISSUE_DEFINITIONS.map(definition => {
      const unavailable = definition.field === 'hasMisspelling' && this.auditDetails?.crawlOptions?.checkSpell !== true || definition.field === 'brokenResources' && this.auditDetails?.crawlOptions?.loadResources !== true;
      const raw: unknown = definition.checks ? this.auditDetails?.pageMetrics?.checks?.[definition.field] : this.auditDetails?.pageMetrics?.[definition.field];
      const count = !unavailable && typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
      const positiveCheck = definition.field === 'seoFriendlyUrl';
      const title = this.translate.instant(definition.label);
      return {
        name: title + ': ' + (count === null ? 'Not checked' : count), title,
        indicator: count === null ? 'NOT_CHECKED' : positiveCheck ? 'NOTICE' : count === 0 ? 'PASSED' : definition.severity,
        description: count,
        filter: count === null || positiveCheck || count === 0 ? '' : definition.field === 'hasMisspelling' ? "['checks.has_misspelling', '=', true]" : definition.filter,
        howToFixPath: definition.help,
        details: definition.field === 'redirectLoop' ? 'Provider-reported loop count. Inspect redirect-chain evidence for saved paths; chains are not automatically loops.' : positiveCheck ? 'Pages passing the URL checks; this is informational.' : count === null ? 'No result was reported for this check.' : undefined
      };
    });
  }

  public get scanedPages() {
    return this._scanedPages;
  }

  public set scanedPages(value) {
    this._scanedPages = value;
  }
}
